/*
  NOURION Kopfmenue: alle Menuepunkte zeigen, sobald sie ohne den Knopf "Mehr" in die Zeile passen
  (Fehlerbehebung 15.10.2026, Kerem: zwischen 768 und 1099 px stand "Mehr" statt "Über NOURION", obwohl Platz war).

  Ursache: Horizons overflow-list (assets/overflow-list.js) stellt beim Messen den Knopf "Mehr" an den Zeilenanfang
  und verschiebt jeden Punkt, der danach umbricht, in das Untermenue. Damit gilt "alles passt" erst, wenn alle Punkte
  UND der Mehr Knopf (rund 55 px) in der Zeile Platz haben. Mit drei Punkten (rund 293 px) zeigte die Kopfzeile deshalb
  zwischen 829 und 884 px sowie 1044 und 1099 px "Mehr", obwohl alle Punkte frei nebeneinander passten; mit dem
  geplanten Menue aus vier Punkten (Klickliste v11) betrifft das Fenster rund 365 bis 420 px Menuebreite.

  Behebung ohne Eingriff in die Horizon Datei: Hat Horizon Punkte ausgelagert, zeigt dieses Skript probeweise alle
  (oeffentliche Methode showAll: Attribut disabled, Mehr ausgeblendet) und prueft mit demselben Kriterium wie Horizon,
  ob ein Punkt in eine zweite Zeile umbricht. Passt alles, bleibt es dabei; sonst uebernimmt Horizon wieder
  (disabled entfernen, Horizon misst neu). Messen und Zuruecksetzen laufen im selben Bild, es flackert nichts.
  Ausloeser: Horizons eigene Messung (Stilattribut --overflow-count), Groessenaenderung der linken Kopfspalte und das
  Nachladen der Schriften. Ohne Horizon Komponente oder auf dem Handy (Menue in der Schublade, nicht sichtbar) tut das
  Skript nichts.

  Sichtbarkeit: checkVisibility() gibt es in Safari erst ab 17.4; aeltere Safari warfen bei jeder Pruefung einen
  TypeError und die Behebung griff dort nie (16.10.2026). Rueckfall: getClientRects().length > 0 (kein Kasten heisst
  display none, auch bei Vorfahren). Im Theme Editor laedt shopify:section:load die Kopfzeile neu; die neue Liste wird
  dann ebenfalls eingerichtet, ein WeakSet merkt sich jede fertig eingerichtete Liste, damit nichts doppelt laeuft.
*/
(() => {
  const ALLE = 'data-nrn-alle';
  const eingerichtet = new WeakSet();

  /** @param {Element} el */
  const sichtbar = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.getClientRects().length > 0);

  /** @param {HTMLElement & { showAll?: () => void }} liste */
  const einrichten = (liste) => {
    const wurzel = liste.shadowRoot;
    const spalte = liste.closest('.header__column') || liste.parentElement;
    if (!wurzel || !spalte || typeof liste.showAll !== 'function') return;
    if (eingerichtet.has(liste)) return;
    eingerichtet.add(liste);

    let geplant = false;
    let gescheitert = '';                       // Zustand, fuer den "passt nicht" schon festgestellt ist

    const punkte = () => [...liste.children].filter((e) => e.tagName === 'LI' && e.getAttribute('slot') !== 'more');

    // Wie Horizon: umbricht ein Punkt in eine zweite Zeile, passt er nicht
    const allePassen = () => {
      const p = punkte();
      if (!p.length) return true;
      const oben = p[0].getBoundingClientRect().top;
      return p.every((e) => Math.abs(e.getBoundingClientRect().top - oben) < 1);
    };

    const anHorizonZurueck = () => {
      liste.removeAttribute(ALLE);
      const platzhalter = wurzel.querySelector('[part="placeholder"]');
      // showAll() hat den Platzhalter per Inline Stil versteckt; Horizon braucht ihn, damit die Liste bei mehr
      // Platz wieder waechst und neu misst
      platzhalter?.style.removeProperty('display');
      platzhalter?.style.removeProperty('width');
      liste.removeAttribute('disabled');        // Horizon: attributeChangedCallback misst neu und beobachtet wieder
    };

    const pruefe = () => {
      geplant = false;
      if (!liste.isConnected || !sichtbar(liste)) return;
      const zustand = `${Math.round(spalte.getBoundingClientRect().width)}|${punkte().length}|${document.fonts?.status}`;
      // Sicherung: hat Horizon trotz disabled noch eine vorgemerkte Messung ausgefuehrt, liegen Punkte im Untermenue,
      // waehrend Mehr ausgeblendet ist. Dann sauber an Horizon zurueckgeben und spaeter neu pruefen.
      if (liste.hasAttribute(ALLE) && liste.querySelector(':scope > [slot="overflow"]')) {
        anHorizonZurueck();
        planen();
        return;
      }
      if (liste.hasAttribute(ALLE)) {
        // Alle Punkte stehen bereits: wird es zu eng, uebernimmt Horizon wieder
        if (!allePassen()) { gescheitert = zustand; anHorizonZurueck(); }
        return;
      }
      // Horizon hat noch nicht gemessen oder zeigt ohnehin alle
      if (!liste.style.getPropertyValue('--overflow-count')) return;
      if (!liste.querySelector(':scope > [slot="overflow"]')) return;
      if (zustand === gescheitert) return;
      liste.showAll();
      liste.setAttribute(ALLE, '');
      if (!allePassen()) { gescheitert = zustand; anHorizonZurueck(); }
    };

    // Gleicher Takt wie Horizons eigene Messung (requestAnimationFrame, dann setTimeout 0), aber danach angemeldet:
    // so laeuft die Pruefung erst, wenn eine vorgemerkte Horizon Messung fertig ist. Sonst misst Horizon nach showAll()
    // mit ausgeblendetem Mehr Knopf, verschiebt alle Punkte ins Untermenue und schaltet auf die Schublade um.
    const planen = () => {
      if (geplant) return;
      geplant = true;
      requestAnimationFrame(() => setTimeout(pruefe, 0));
    };

    new MutationObserver(planen).observe(liste, { attributes: true, attributeFilter: ['style'] });
    new ResizeObserver(planen).observe(spalte);
    document.fonts?.ready.then(planen);
    planen();
  };

  /** @param {ParentNode} bereich */
  const start = (bereich) => (bereich.querySelectorAll ? bereich.querySelectorAll('header-menu overflow-list') : []).forEach(einrichten);
  if (!window.customElements) return;
  customElements.whenDefined('overflow-list').then(() => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document), { once: true });
    else start(document);
    // Theme Editor: neu geladene Kopfzeile (die Liste darin ist frisch, das WeakSet kennt sie noch nicht)
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target) start(e.target);
    });
  });
})();
